import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { UsersRound } from "lucide-react";

const COLORS = ["#3b82f6", "#ec4899", "#94a3b8"];

function GenderTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const value = Number(item.value || 0);
  const percent = total ? Math.round((value / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-xl backdrop-blur">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.payload.fill }} />
        <span className="text-sm font-medium text-slate-600">{item.name}</span>
      </div>
      <div className="mt-1 flex items-end gap-2">
        <span className="text-xl font-semibold text-slate-900">{value.toLocaleString("th-TH")}</span>
        <span className="pb-0.5 text-xs text-slate-400">คน · {percent}%</span>
      </div>
    </div>
  );
}

export default function GenderPieChart({ data }) {
  const chartData = (Array.isArray(data) ? data : []).map((item, index) => ({
    ...item,
    value: Number(item.value || 0),
    fill: COLORS[index % COLORS.length],
  }));
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-gradient-to-br from-teal-100/70 to-cyan-50/60 blur-2xl" />

      <div className="relative flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">สัดส่วนผู้เข้าใช้บริการ</h3>
          <p className="mt-1 text-xs text-slate-500">เปรียบเทียบจำนวนแยกตามเพศ</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-2.5 text-slate-500 ring-1 ring-slate-100">
          <UsersRound size={19} />
        </div>
      </div>

      <div className="relative mt-2 grid min-h-[270px] grid-cols-1 items-center sm:grid-cols-[1.1fr_0.9fr]">
        <div className="relative h-64 min-h-[256px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={94}
                paddingAngle={4}
                cornerRadius={8}
                stroke="none"
              >
                {chartData.map((item, index) => <Cell key={`${item.label}-${index}`} fill={item.fill} />)}
              </Pie>
              <Tooltip content={<GenderTooltip total={total} />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xs font-medium text-slate-400">ทั้งหมด</span>
            <span className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{total.toLocaleString("th-TH")}</span>
            <span className="text-xs text-slate-400">คน</span>
          </div>
        </div>

        <div className="space-y-3 pb-2 sm:pb-0 sm:pr-2">
          {chartData.map((item) => {
            const percent = total ? Math.round((item.value / total) * 100) : 0;
            return (
              <div key={item.label} className="rounded-xl border border-slate-100 bg-slate-50/70 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-600">
                    <i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.fill }} />
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-slate-900">{percent}%</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${percent}%`, backgroundColor: item.fill }} />
                </div>
                <p className="mt-1.5 text-right text-xs text-slate-400">{item.value.toLocaleString("th-TH")} คน</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
